import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import EnquireModal from '../components/EnquireModal';
import HeroSlider from '../components/HeroSlider';
import { productsAPI, categoriesAPI, enquiriesAPI, API_BASE_URL, getImageUrl } from '../utils/api';

const Home = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]); // Separate state for slider
  const [activeCategory, setActiveCategory] = useState('all'); // Default to showing all products
  const [enquireModalOpen, setEnquireModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);

  // Contact Form State
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [contactStatus, setContactStatus] = useState(null); // 'success', 'error', null
  const [contactLoading, setContactLoading] = useState(false);

  const handleContactChange = (e) => {
    setContactForm({ ...contactForm, [e.target.name]: e.target.value });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    setContactStatus(null);
    try {
      await enquiriesAPI.create({ ...contactForm, type: 'general' });
      setContactStatus('success');
      setContactForm({ name: '', email: '', phone: '', company: '', message: '' });
      setTimeout(() => setContactStatus(null), 5000);
    } catch (error) {
      console.error('Contact submit error:', error);
      setContactStatus('error');
    } finally {
      setContactLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // First, test backend connectivity
        console.log('Testing backend connectivity...');
        try {
          const healthCheck = await fetch(`${API_BASE_URL}/health`);
          if (healthCheck.ok) {
            const healthData = await healthCheck.json();
            console.log('✅ Backend is accessible:', healthData);
          } else {
            console.warn('⚠️ Backend responded with error status:', healthCheck.status);
          }
        } catch (healthError) {
          console.error('❌ Backend health check failed:', healthError.message);
          console.error('   Make sure the backend server is running on http://localhost:5000');
        }

        console.log('Fetching products and categories...');
        const [productsRes, categoriesRes, featuredRes] = await Promise.all([
          productsAPI.getAll({ status: 'published' }), // Fetch all published products
          categoriesAPI.getAll({ status: 'active' }),
          productsAPI.getAll({ status: 'published', featured: true }) // Fetch all featured products
        ]);

        console.log('Products response:', productsRes);
        console.log('Categories response:', categoriesRes);
        console.log('Products data:', productsRes.data);
        console.log('Categories data:', categoriesRes.data);

        // Handle both array and object responses (checking for .products, .categories, or .data)
        const products = Array.isArray(productsRes.data) ? productsRes.data : (productsRes.data?.products || productsRes.data?.data || []);
        const categories = Array.isArray(categoriesRes.data) ? categoriesRes.data : (categoriesRes.data?.categories || categoriesRes.data?.data || []);

        console.log('Setting products:', products.length);
        console.log('Setting categories:', categories.length);

        setProducts(products);
        setCategories(categories);
        const featuredData = Array.isArray(featuredRes.data) ? featuredRes.data : (featuredRes.data?.products || featuredRes.data?.data || []);
        console.log('🎯 Featured products fetched:', featuredData.length, featuredData);
        setFeaturedProducts(featuredData);

        // Auto-select first relevant category if current isn't valid
        if (featuredData.length > 0) {
          const featuredCatIds = new Set(featuredData.map(p =>
            typeof p.category === 'object' ? p.category._id : p.category
          ));
          const relevantCategories = categories.filter(cat => featuredCatIds.has(cat._id));
          if (relevantCategories.length > 0 && activeCategory === 'all') {
            const firstSlug = relevantCategories[0].slug || relevantCategories[0].name?.toLowerCase().replace(/\s+/g, '-');
            setActiveCategory(firstSlug);
          }
        }
      } catch (error) {
        console.error('❌ Error fetching data:', error);
        if (error.response) {
          // Server responded with error status
          console.error('Response error:', {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data
          });
        } else if (error.request) {
          // Request was made but no response received
          console.error('❌ No response from server:', {
            url: error.config?.url,
            baseURL: error.config?.baseURL,
            message: 'Backend server may not be running or CORS issue'
          });
        } else {
          // Error setting up request
          console.error('Request setup error:', error.message);
        }
        console.error('Full error:', error);
      } finally {
        setLoading(false);
        setLoadingCategories(false);
      }
    };
    fetchData();
  }, []);

  // Mouse tracking for parallax effects
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100
      });
    };

    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);

    window.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseenter', handleMouseEnter);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseenter', handleMouseEnter);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const showCategory = (categorySlug) => {
    setActiveCategory(categorySlug);
  };

  // Group products by category slug
  const getCategorySlug = (category) => {
    if (!category) {
      console.warn('Product has no category');
      return 'other';
    }

    // Handle both populated category object and category ID
    const categoryObj = typeof category === 'object' ? category : null;
    // Just use the slug if it exists, otherwise generate one from name
    return categoryObj?.slug || categoryObj?.name?.toLowerCase().replace(/\s+/g, '-') || 'other';
  };

  // Group products by category
  const productsByCategory = products.reduce((acc, product) => {
    const categorySlug = getCategorySlug(product.category);
    if (!acc[categorySlug]) {
      acc[categorySlug] = [];
    }
    acc[categorySlug].push(product);
    return acc;
  }, {});

  // Debug logging
  useEffect(() => {
    if (products.length > 0) {
      console.log('Products by category:', productsByCategory);
      console.log('Active category:', activeCategory);
      console.log('Active products:', productsByCategory[activeCategory] || []);
    }
  }, [products, activeCategory, productsByCategory]);

  // Get products for active category (or all if 'all' is selected)
  const activeProducts = activeCategory === 'all' ? products : (productsByCategory[activeCategory] || []);

  // Auto-switch disabled - now defaulting to 'all' products
  // useEffect(() => {
  //   if (!loading && products.length > 0) {
  //     const currentProducts = productsByCategory[activeCategory] || [];
  //     if (currentProducts.length === 0) {
  //       const availableCategories = Object.keys(productsByCategory);
  //       if (availableCategories.length > 0) {
  //         const bestCategory = availableCategories.find(cat => productsByCategory[cat].length > 0);
  //         if (bestCategory) {
  //           setActiveCategory(bestCategory);
  //         }
  //       }
  //     }
  //   }
  // }, [loading, products, activeCategory, productsByCategory]);


  // Get featured products for slider (limit 6) - Now using separate state from API
  // Get featured products for slider (limit 6) - Now using separate state from API
  // const featuredProducts = products.filter(p => p.featured).slice(0, 6);

  return (
    <div style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>

      <HeroSlider featuredProducts={featuredProducts} />

      {/* Certifications Strip */}
      <section className="bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 py-4 certifications-strip relative overflow-hidden border-y border-slate-300/50">
        {/* Animated background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0,0,0,0.1) 10px, rgba(0,0,0,0.1) 20px)'
          }}></div>
        </div>

        <div className="mx-auto px-4 sm:px-6 lg:px-8 relative z-10" style={{ maxWidth: '90%' }}>
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 lg:gap-8">
            <div className="flex items-center gap-2.5 group cursor-pointer bg-white px-4 py-2.5 rounded-lg border-2 border-db-blue/30 hover:border-db-blue hover:shadow-lg transition-all duration-300 hover:scale-105 hover:-translate-y-1" data-aos="fade-up" data-aos-delay="0">
              <div className="w-10 h-10 bg-gradient-to-br from-db-blue to-blue-700 rounded-lg flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                <i className="fas fa-handshake text-lg text-white"></i>
              </div>
              <div>
                <div className="text-xs text-db-blue uppercase tracking-wide font-bold">Epson</div>
                <div className="text-xs font-semibold text-gray-700">Authorized Partner</div>
              </div>
            </div>
            <div className="flex items-center gap-2.5 group cursor-pointer bg-white px-4 py-2.5 rounded-lg border-2 border-db-red/30 hover:border-db-red hover:shadow-lg transition-all duration-300 hover:scale-105 hover:-translate-y-1" data-aos="fade-up" data-aos-delay="100">
              <div className="w-10 h-10 bg-gradient-to-br from-db-red to-red-700 rounded-lg flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                <i className="fas fa-handshake text-lg text-white"></i>
              </div>
              <div>
                <div className="text-xs text-db-red uppercase tracking-wide font-bold">HP</div>
                <div className="text-xs font-semibold text-gray-700">Certified Reseller</div>
              </div>
            </div>
            <div className="flex items-center gap-2.5 group cursor-pointer bg-white px-4 py-2.5 rounded-lg border-2 border-green-500/30 hover:border-green-500 hover:shadow-lg transition-all duration-300 hover:scale-105 hover:-translate-y-1" data-aos="fade-up" data-aos-delay="200">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-700 rounded-lg flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                <i className="fas fa-handshake text-lg text-white"></i>
              </div>
              <div>
                <div className="text-xs text-green-600 uppercase tracking-wide font-bold">Canon</div>
                <div className="text-xs font-semibold text-gray-700">Authorized Dealer</div>
              </div>
            </div>
            <div className="flex items-center gap-2.5 group cursor-pointer bg-white px-4 py-2.5 rounded-lg border-2 border-yellow-500/30 hover:border-yellow-500 hover:shadow-lg transition-all duration-300 hover:scale-105 hover:-translate-y-1" data-aos="fade-up" data-aos-delay="300">
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-yellow-700 rounded-lg flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                <i className="fas fa-handshake text-lg text-white"></i>
              </div>
              <div>
                <div className="text-xs text-yellow-600 uppercase tracking-wide font-bold">Zebra</div>
                <div className="text-xs font-semibold text-gray-700">Partner Program</div>
              </div>
            </div>
            <div className="flex items-center gap-2.5 group cursor-pointer bg-white px-4 py-2.5 rounded-lg border-2 border-purple-500/30 hover:border-purple-500 hover:shadow-lg transition-all duration-300 hover:scale-105 hover:-translate-y-1" data-aos="fade-up" data-aos-delay="400">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-700 rounded-lg flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                <i className="fas fa-handshake text-lg text-white"></i>
              </div>
              <div>
                <div className="text-xs text-purple-600 uppercase tracking-wide font-bold">Honeywell</div>
                <div className="text-xs font-semibold text-gray-700">System Integrator</div>
              </div>
            </div>
          </div>
        </div>
      </section>


      <section id="featured-products" className="py-16 bg-white" style={{ overflow: 'visible', overflowY: 'visible' }}>
        <div className="mx-auto px-4 sm:px-6 lg:px-8 relative z-10" style={{ maxWidth: '90%', overflow: 'visible', overflowY: 'visible' }}>
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl sm:text-4xl font-bold text-industrial-navy mb-4">Featured Products by Category</h2>
            <p className="text-lg text-gray-600">Explore our comprehensive range of industrial automation solutions</p>
          </div>

          {/* Category Tabs - Dynamic from Database */}
          <div className="flex flex-wrap justify-center gap-3 mb-12" data-aos="fade-up" data-aos-delay="200">
            {loadingCategories ? (
              <div className="text-center py-4">
                <i className="fas fa-spinner fa-spin text-xl text-gray-400"></i>
              </div>
            ) : categories.length === 0 ? (
              <p className="text-gray-500">No categories available</p>
            ) : (
              (() => {
                // Smart Filter: Get IDs of categories that have at least one featured product
                const featuredCatIds = new Set(featuredProducts.map(p =>
                  typeof p.category === 'object' ? p.category._id : p.category
                ));

                // Filter categories list
                const relevantCategories = categories.filter(cat => featuredCatIds.has(cat._id));

                if (relevantCategories.length === 0) {
                  return <p className="text-gray-500">No featured categories found</p>;
                }

                return relevantCategories.map((category, index) => {
                  const categorySlug = category.slug || category.name?.toLowerCase().replace(/\s+/g, '-') || '';
                  const getCatIcon = (name) => {
                    const n = name?.toLowerCase() || '';
                    if (n.includes('barcode printer')) return 'fas fa-barcode';
                    if (n.includes('thermal printer')) return 'fas fa-print';
                    if (n.includes('label')) return 'fas fa-tag';
                    if (n.includes('roll')) return 'fas fa-scroll';
                    if (n.includes('scanner')) return 'fas fa-qrcode';
                    if (n.includes('ribbon')) return 'fas fa-tape';
                    return 'fas fa-box';
                  };
                  return (
                    <button
                      key={category._id || index}
                      onClick={() => showCategory(categorySlug)}
                      className={`category-tab px-6 py-3 rounded-lg font-semibold transition ${activeCategory === categorySlug
                        ? 'bg-db-blue text-white shadow-md'
                        : 'bg-gray-100 hover:bg-db-blue hover:text-white'
                        }`}
                    >
                      <i className={`${getCatIcon(category.name)} mr-2`}></i>{category.name}
                    </button>
                  );
                });
              })()
            )}
          </div>

          {loading ? (
            <div className="text-center py-12">
              <i className="fas fa-spinner fa-spin text-3xl text-gray-400 mb-4"></i>
              <p className="text-gray-600">Loading products...</p>
            </div>
          ) : activeProducts.length === 0 && products.length > 0 && Object.keys(productsByCategory).length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg mb-2">No products found in this category</p>
              <p className="text-gray-500 text-sm mb-4">Showing all products instead</p>
              <div className="product-grid grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {products.slice(0, 6).map((product, index) => (
                  <div
                    key={product._id || index}
                    className="bg-white border border-gray-200 rounded-lg overflow-hidden hover-lift card-shine relative"
                    data-aos="zoom-in"
                    data-aos-delay={index * 100}
                  >
                    {product.featured && <div className="badge-popular">POPULAR</div>}
                    <Link to={`/product/${product._id}`} className="block group/details">
                      <div className="overflow-hidden">
                        <img
                          src={getImageUrl(product.images?.[0]) || 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400'}
                          alt={product.name}
                          className="w-full h-48 object-cover transition-transform duration-500"
                        />
                      </div>
                      <div className="p-6 pb-0">
                        <div className="text-xs text-blue-600 font-semibold mb-2 flex justify-between">
                          <span>{product.category?.name?.toUpperCase() || 'PRODUCT'}</span>
                          {product.brand && <span className="text-db-blue">{product.brand}</span>}
                        </div>
                        <h3 className="text-xl font-bold mb-3 transition-colors">{product.name}</h3>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.shortDescription || product.fullDescription?.substring(0, 100)}</p>
                      </div>
                    </Link>
                    <div className="p-6 pt-2">
                      <div className="flex flex-wrap gap-2 text-xs text-gray-500 mb-4">
                        {product.sku && <span>SKU: {product.sku}</span>}
                        {product.model && <span>| Model: {product.model}</span>}
                      </div>
                      <div className="flex gap-2">
                        <Link
                          to={`/product/${product._id}`}
                          className="flex-1 inline-block bg-db-blue hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition transform hover:scale-105 text-center font-semibold"
                        >
                          Details
                        </Link>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedProduct(product);
                            setEnquireModalOpen(true);
                          }}
                          className="flex-1 bg-gradient-to-r from-db-red to-red-700 hover:from-red-700 hover:to-red-800 text-white px-4 py-2 rounded-lg transition transform hover:scale-105 text-center text-sm font-semibold"
                        >
                          Enquire
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Dynamic Product Grid */}
              <div
                className={`product-grid grid sm:grid-cols-2 lg:grid-cols-3 gap-8 ${activeProducts.length > 0 ? 'block' : 'hidden'}`}
              >
                {activeProducts.map((product, index) => (
                  <div
                    key={product._id}
                    className="bg-white border-2 border-transparent hover:border-blue-500 rounded-lg overflow-hidden relative shadow-sm transition-all duration-300 h-full flex flex-col"
                    data-aos="zoom-in"
                    data-aos-delay={index * 100}
                  >
                    {product.featured && <div className="badge-popular">POPULAR</div>}
                    <div className="overflow-hidden product-image-container">
                      <div className="w-full h-48 bg-gray-50 flex items-center justify-center">
                        {product.images && product.images.length > 0 ? (
                          <img
                            src={getImageUrl(product.images[0])}
                            alt={product.name}
                            className="w-full h-full object-cover image-sharp"
                            style={{
                              imageRendering: 'crisp-edges',
                              backfaceVisibility: 'hidden'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400" style={{ display: product.images && product.images.length > 0 ? 'none' : 'flex' }}>
                          <i className="fas fa-image text-4xl"></i>
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="text-xs text-blue-600 font-semibold mb-2 flex justify-between">
                        <span>{product.category?.name?.toUpperCase() || 'PRODUCT'}</span>
                        {product.brand && <span className="text-db-blue">{product.brand}</span>}
                      </div>
                      <h3 className="text-xl font-bold mb-3 line-clamp-2 min-h-[56px]">{product.name}</h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3 min-h-[60px]">{product.shortDescription || (product.description ? product.description.replace(/<[^>]*>?/gm, '') : '') || 'No description available.'}</p>

                      <div className="flex flex-wrap gap-2 text-xs text-gray-500 mb-2 min-h-[20px]">
                        {product.sku && <span>SKU: {product.sku}</span>}
                        {product.model && <span>| Model: {product.model}</span>}
                      </div>



                      <div className="flex gap-2 mt-auto">
                        <Link
                          to={`/product/${product._id}`}
                          className="flex-1 inline-block bg-db-blue hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition transform hover:scale-105 text-center"
                        >
                          View Details
                        </Link>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedProduct(product);
                            setEnquireModalOpen(true);
                          }}
                          className="flex-1 bg-gradient-to-r from-db-red to-red-700 hover:from-red-700 hover:to-red-800 text-white px-4 py-2 rounded-lg transition transform hover:scale-105 text-center text-sm font-semibold"
                        >
                          <i className="fas fa-envelope mr-1"></i>Enquire
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Show all products if category matching failed */}
              {activeProducts.length === 0 && !loading && products.length > 0 && Object.keys(productsByCategory).length === 0 && (
                <div className="text-center py-12">
                  <div className="mb-6">
                    <i className="fas fa-info-circle text-2xl text-blue-500 mb-2"></i>
                    <p className="text-gray-600 text-lg mb-2">Showing All Products</p>
                    <p className="text-gray-500 text-sm">Products are available but category matching needs adjustment</p>
                  </div>
                  <div className="product-grid grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {products.map((product, index) => (
                      <div
                        key={product._id || index}
                        className="bg-white border border-gray-200 rounded-lg overflow-hidden hover-lift card-shine relative"
                        data-aos="zoom-in"
                        data-aos-delay={index * 100}
                      >
                        {product.featured && <div className="badge-popular">POPULAR</div>}
                        <div className="overflow-hidden">
                          <img
                            src={getImageUrl(product.images?.[0]) || 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400'}
                            alt={product.name}
                            className="w-full h-48 object-cover transition-transform duration-500 hover:scale-110"
                          />
                        </div>
                        <div className="p-6">
                          <div className="text-xs text-blue-600 font-semibold mb-2 flex justify-between">
                            <span>{product.category?.name?.toUpperCase() || 'PRODUCT'}</span>
                            {product.brand && <span className="text-db-blue">{product.brand}</span>}
                          </div>
                          <h3 className="text-xl font-bold mb-3">{product.name}</h3>
                          <p className="text-gray-600 text-sm mb-4">{product.shortDescription || product.fullDescription?.substring(0, 100)}</p>
                          <div className="flex flex-wrap gap-2 text-xs text-gray-500 mb-2">
                            {product.sku && <span>SKU: {product.sku}</span>}
                            {product.model && <span>| Model: {product.model}</span>}
                          </div>
                          {product.keyFeatures && product.keyFeatures.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                              {product.keyFeatures.slice(0, 3).map((feature, idx) => (
                                <span key={idx} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                  {feature}
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="flex gap-2">
                            <Link
                              to={`/product/${product._id}`}
                              className="flex-1 inline-block bg-db-blue hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition transform hover:scale-105 text-center"
                            >
                              View Details
                            </Link>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                setSelectedProduct(product);
                                setEnquireModalOpen(true);
                              }}
                              className="flex-1 bg-gradient-to-r from-db-red to-red-700 hover:from-red-700 hover:to-red-800 text-white px-4 py-2 rounded-lg transition transform hover:scale-105 text-center text-sm font-semibold"
                            >
                              <i className="fas fa-envelope mr-1"></i>Enquire
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Show message if no products in selected category but other categories have products */}
              {activeProducts.length === 0 && !loading && products.length > 0 && Object.keys(productsByCategory).length > 0 && (
                <div className="text-center py-12">
                  <i className="fas fa-box-open text-4xl text-gray-400 mb-4"></i>
                  <p className="text-gray-600 text-lg mb-2">No products found in this category</p>
                  <p className="text-gray-500 text-sm mb-4">Try selecting a different category</p>
                </div>
              )}
              {/* Show message if no products at all */}
              {activeProducts.length === 0 && !loading && products.length === 0 && (
                <div className="text-center py-12">
                  <i className="fas fa-box-open text-4xl text-gray-400 mb-4"></i>
                  <p className="text-gray-600 text-lg mb-2">No products available</p>
                  <p className="text-gray-500 text-sm">Please check your backend connection and ensure products are seeded.</p>
                </div>
              )}
            </>
          )}

          {products.length > 0 && (
            <div className="text-center mt-12 space-x-4 relative z-10" data-aos="fade-up" style={{ pointerEvents: 'auto' }}>
              <Link
                to="/products"
                className="inline-block bg-gradient-to-r from-db-blue to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 rounded-lg font-semibold transition transform hover:scale-105 shadow-lg relative z-20"
                style={{ pointerEvents: 'auto', position: 'relative', zIndex: 20 }}
              >
                <i className="fas fa-th-large mr-2"></i>View All Products
              </Link>
              <a
                href="#contact"
                className="inline-block bg-gradient-to-r from-db-red to-red-700 hover:from-red-700 hover:to-red-800 text-white px-8 py-4 rounded-lg font-semibold transition transform hover:scale-105 shadow-lg relative z-20"
                style={{ pointerEvents: 'auto', position: 'relative', zIndex: 20 }}
              >
                <i className="fas fa-envelope mr-2"></i>Request Product Information
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Our Process Section */}
      <section className="py-12 bg-gradient-to-br from-gray-50 to-white our-process-section">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 relative z-10" style={{ maxWidth: '90%' }}>
          <div className="text-center mb-16" data-aos="fade-up">
            <div className="inline-block bg-db-red/10 text-db-red px-4 py-2 rounded-full text-sm font-semibold mb-4">
              HOW WE WORK
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-industrial-navy mb-4">Our Process</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">From initial consultation to final delivery, we ensure a seamless experience at every step</p>
          </div>

          <div className="relative">
            {/* Process Timeline */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Step 1 */}
              <div
                className="relative process-step"
                data-aos="fade-up"
                data-aos-delay="0"
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px) scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                }}
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 h-full border-2 border-transparent hover:border-db-blue relative z-10">
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                    <div className="w-12 h-12 bg-gradient-to-br from-db-blue to-blue-700 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                      1
                    </div>
                  </div>
                  <div className="mt-6 text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-comments text-3xl text-db-blue"></i>
                    </div>
                    <h3 className="text-xl font-bold text-industrial-navy mb-3">Consultation</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      We start by understanding your business needs, requirements, and challenges through detailed consultation sessions.
                    </p>
                  </div>
                </div>
                <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-0">
                  <i className="fas fa-arrow-right text-2xl text-db-blue opacity-50"></i>
                </div>
              </div>

              {/* Step 2 */}
              <div
                className="relative process-step"
                data-aos="fade-up"
                data-aos-delay="100"
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px) scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                }}
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 h-full border-2 border-transparent hover:border-db-red relative z-10">
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                    <div className="w-12 h-12 bg-gradient-to-br from-db-red to-red-700 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                      2
                    </div>
                  </div>
                  <div className="mt-6 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-clipboard-check text-3xl text-db-red"></i>
                    </div>
                    <h3 className="text-xl font-bold text-industrial-navy mb-3">Solution Design</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Our experts design a customized solution tailored to your specific needs, including product recommendations and system integration.
                    </p>
                  </div>
                </div>
                <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-0">
                  <i className="fas fa-arrow-right text-2xl text-db-red opacity-50"></i>
                </div>
              </div>

              {/* Step 3 */}
              <div
                className="relative process-step"
                data-aos="fade-up"
                data-aos-delay="200"
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px) scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                }}
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 h-full border-2 border-transparent hover:border-green-500 relative z-10">
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                      3
                    </div>
                  </div>
                  <div className="mt-6 text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-shipping-fast text-3xl text-green-600"></i>
                    </div>
                    <h3 className="text-xl font-bold text-industrial-navy mb-3">Fast Delivery</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      We ensure quick order processing and fast delivery with real-time tracking, so you receive your products on time.
                    </p>
                  </div>
                </div>
                <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-0">
                  <i className="fas fa-arrow-right text-2xl text-green-500 opacity-50"></i>
                </div>
              </div>

              {/* Step 4 */}
              <div
                className="relative process-step"
                data-aos="fade-up"
                data-aos-delay="300"
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px) scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                }}
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 h-full border-2 border-transparent hover:border-purple-500 relative z-10">
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-700 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                      4
                    </div>
                  </div>
                  <div className="mt-6 text-center">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-headset text-3xl text-purple-600"></i>
                    </div>
                    <h3 className="text-xl font-bold text-industrial-navy mb-3">Ongoing Support</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      We provide continuous support, training, and maintenance to ensure your systems operate at peak performance.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Process Benefits */}
            <div className="mt-16 grid md:grid-cols-3 gap-6" data-aos="fade-up" data-aos-delay="400">
              <div className="bg-gradient-to-br from-db-blue/10 to-blue-50 rounded-xl p-6 text-center">
                <i className="fas fa-clock text-3xl text-db-blue mb-3"></i>
                <h4 className="font-bold text-industrial-navy mb-2">Quick Response</h4>
                <p className="text-sm text-gray-600">24-hour response time for all inquiries</p>
              </div>
              <div className="bg-gradient-to-br from-db-red/10 to-red-50 rounded-xl p-6 text-center">
                <i className="fas fa-handshake text-3xl text-db-red mb-3"></i>
                <h4 className="font-bold text-industrial-navy mb-2">Expert Guidance</h4>
                <p className="text-sm text-gray-600">Dedicated account managers for personalized service</p>
              </div>
              <div className="bg-gradient-to-br from-green-500/10 to-green-50 rounded-xl p-6 text-center">
                <i className="fas fa-check-circle text-3xl text-green-600 mb-3"></i>
                <h4 className="font-bold text-industrial-navy mb-2">Quality Assurance</h4>
                <p className="text-sm text-gray-600">Rigorous testing and quality checks before delivery</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 relative z-10" style={{ maxWidth: '90%' }}>
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl sm:text-4xl font-bold text-industrial-navy mb-4">Product Highlights</h2>
            <p className="text-lg text-gray-600">Industry-leading barcode solutions</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-lg hover-lift" data-aos="fade-up" data-aos-delay="0">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 floating">
                <i className="fas fa-shield-alt text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold mb-3">Reliability First</h3>
              <p className="text-gray-600">Mission-critical systems designed for 24/7 operation in harsh industrial environments with proven uptime records.</p>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg hover-lift" data-aos="fade-up" data-aos-delay="200">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 floating" style={{ animationDelay: '0.5s' }}>
                <i className="fas fa-cogs text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold mb-3">Engineering Excellence</h3>
              <p className="text-gray-600">Advanced SCADA expertise with support for IEC 104, DNP3, Modbus, and custom protocol integration.</p>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg hover-lift" data-aos="fade-up" data-aos-delay="400">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 floating" style={{ animationDelay: '1s' }}>
                <i className="fas fa-globe text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold mb-3">Global Support</h3>
              <p className="text-gray-600">Comprehensive technical support, training, and maintenance services across all major industrial regions.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 relative z-10" style={{ maxWidth: '90%' }}>
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl sm:text-4xl font-bold text-industrial-navy mb-4">Industries We Serve</h2>
            <p className="text-lg text-gray-600">Trusted by leading organizations worldwide</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-lg hover-lift card-shine" data-aos="zoom-in" data-aos-delay="0">
              <div className="overflow-hidden rounded-lg mb-4">
                <img src="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=400" alt="Power Industry" className="w-full h-48 object-cover transition-transform duration-500 hover:scale-110" />
              </div>
              <h3 className="text-xl font-bold mb-2">Power & Utilities</h3>
              <p className="text-gray-600 text-sm">Substation automation, grid monitoring, and energy management systems.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg hover-lift card-shine" data-aos="zoom-in" data-aos-delay="100">
              <div className="overflow-hidden rounded-lg mb-4">
                <img src="https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400" alt="Water Industry" className="w-full h-48 object-cover transition-transform duration-500 hover:scale-110" />
              </div>
              <h3 className="text-xl font-bold mb-2">Water & Wastewater</h3>
              <p className="text-gray-600 text-sm">Treatment plant automation, distribution monitoring, and SCADA integration.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg hover-lift card-shine" data-aos="zoom-in" data-aos-delay="200">
              <div className="overflow-hidden rounded-lg mb-4">
                <img src="https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=400" alt="Oil and Gas" className="w-full h-48 object-cover transition-transform duration-500 hover:scale-110" />
              </div>
              <h3 className="text-xl font-bold mb-2">Oil & Gas</h3>
              <p className="text-gray-600 text-sm">Pipeline monitoring, remote site control, and safety system integration.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg hover-lift card-shine" data-aos="zoom-in" data-aos-delay="300">
              <div className="overflow-hidden rounded-lg mb-4">
                <img src="https://images.unsplash.com/photo-1509391366360-2e959784a276?w=400" alt="Renewables" className="w-full h-48 object-cover transition-transform duration-500 hover:scale-110" />
              </div>
              <h3 className="text-xl font-bold mb-2">Renewables</h3>
              <p className="text-gray-600 text-sm">Solar, wind, and hybrid energy system monitoring and control solutions.</p>
            </div>
          </div>
        </div>
      </section>



      <section id="contact" className="py-16 bg-gray-50">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 relative z-10" style={{ maxWidth: '90%' }}>
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl sm:text-4xl font-bold text-industrial-navy mb-4">Contact Us</h2>
            <p className="text-lg text-gray-600">Get in touch with our team for inquiries, quotes, or technical support</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div data-aos="fade-right">
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <h3 className="text-2xl font-bold mb-6">Send Us a Message</h3>
                <form onSubmit={handleContactSubmit} className="space-y-6">
                  {contactStatus === 'success' && (
                    <div className="p-4 bg-green-100 text-green-700 rounded-lg">
                      <i className="fas fa-check-circle mr-2"></i> Message sent successfully! We'll get back to you soon.
                    </div>
                  )}
                  {contactStatus === 'error' && (
                    <div className="p-4 bg-red-100 text-red-700 rounded-lg">
                      <i className="fas fa-exclamation-circle mr-2"></i> Failed to send message. Please try again.
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-semibold mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={contactForm.name}
                      onChange={handleContactChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 transition"
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={contactForm.email}
                      onChange={handleContactChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 transition"
                      placeholder="john@company.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={contactForm.phone}
                      onChange={handleContactChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 transition"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Company Name</label>
                    <input
                      type="text"
                      name="company"
                      value={contactForm.company}
                      onChange={handleContactChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 transition"
                      placeholder="Your Company"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Message *</label>
                    <textarea
                      rows="5"
                      name="message"
                      value={contactForm.message}
                      onChange={handleContactChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 transition"
                      placeholder="Tell us about your project requirements..."
                      required
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={contactLoading}
                    className="w-full bg-db-red hover:bg-red-700 text-white py-3 rounded-lg font-semibold transition transform hover:scale-105 disabled:opacity-50"
                  >
                    {contactLoading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>

            <div data-aos="fade-left">
              <div className="bg-white p-8 rounded-lg shadow-lg mb-8">
                <h3 className="text-2xl font-bold mb-6">Office Location</h3>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <i className="fas fa-map-marker-alt text-blue-600 text-xl mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold mb-1">Pune Office</h4>
                      <p className="text-gray-600">Plot No. 14, Anand Baug Society,<br />Near Pathak Baug, Navi Peth,<br />Pune - 411030.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="fas fa-phone text-blue-600 text-xl mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold mb-1">Contact Number</h4>
                      <p className="text-gray-600">Sanjay Mali: +91 9028200666</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="fas fa-envelope text-blue-600 text-xl mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold mb-1">Email</h4>
                      <p className="text-gray-600">digitalbarcodepune@gmail.com</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="fas fa-clock text-blue-600 text-xl mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold mb-1">Business Hours</h4>
                      <p className="text-gray-600">Monday - Saturday<br /></p>
                    </div>
                  </div>
                </div>
              </div>


            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-br from-db-dark to-blue-900 text-white">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 relative z-10" style={{ maxWidth: '90%' }}>
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Start Your Automation Journey Today</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">Join hundreds of satisfied clients who trust us for their critical automation needs. Our team is ready to help you design, implement, and support your next project.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#contact" className="bg-white text-db-dark hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition inline-flex items-center justify-center transform hover:scale-105">
                <i className="fas fa-envelope mr-2"></i>Request a Consultation
              </a>
              <a href="#featured-products" className="bg-db-red hover:bg-red-700 px-8 py-4 rounded-lg font-semibold transition inline-flex items-center justify-center transform hover:scale-105">
                <i className="fas fa-box-open mr-2"></i>Explore Products
              </a>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div data-aos="zoom-in" data-aos-delay="0">
              <div className="text-4xl font-bold mb-2">500+</div>
              <p className="text-gray-300">Projects Completed</p>
            </div>
            <div data-aos="zoom-in" data-aos-delay="100">
              <div className="text-4xl font-bold mb-2">20+</div>
              <p className="text-gray-300">Years Experience</p>
            </div>
            <div data-aos="zoom-in" data-aos-delay="200">
              <div className="text-4xl font-bold mb-2">99.98%</div>
              <p className="text-gray-300">System Uptime</p>
            </div>
            <div data-aos="zoom-in" data-aos-delay="300">
              <div className="text-4xl font-bold mb-2">24/7</div>
              <p className="text-gray-300">Technical Support</p>
            </div>
          </div>
        </div>
      </section>

      {/* Enquire Modal */}
      <EnquireModal
        isOpen={enquireModalOpen}
        onClose={() => {
          setEnquireModalOpen(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
      />
    </div>
  );
};

export default Home;

